import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { getClients, getClientActivityLogs } from "../../lib/api";
import { PageLoader } from "../../components/ui/spinner";
import { SessionsModeToggle } from "./SessionsModeToggle";

// " · 3 alarms handled" / " · 1 alarm handled" / " · no alarms".
// A function rather than a ternary inside a template literal inside a ternary (S3358).
function alarmsSummary(alarmsHandled) {
  if (!(alarmsHandled > 0)) return " · no alarms";
  const plural = alarmsHandled === 1 ? "" : "s";
  return ` · ${alarmsHandled} alarm${plural} handled`;
}

function formatLogDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { month: "2-digit", day: "2-digit" });
}

export default function ClientActivitySessionsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    const requestedId = searchParams.get("client");
    getClients()
      .then((data) => {
        setClients(data);
        const requested = requestedId && data.find((c) => c.id === requestedId);
        if (requested) setSelectedId(requested.id);
        else if (data.length > 0) setSelectedId(data[0].id);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLogsLoading(true);
    getClientActivityLogs(selectedId)
      .then(setLogs)
      .finally(() => setLogsLoading(false));
  }, [selectedId]);

  const filtered = clients.filter((c) =>
    !search ||
    c.id.toLowerCase().includes(search.toLowerCase()) ||
    (c.name || "").toLowerCase().includes(search.toLowerCase())
  );
  const selectedClient = clients.find((c) => c.id === selectedId);

  if (loading) return <PageLoader />;

  return (
    <div className="flex flex-col gap-0">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Activity Sessions · Client</h1>
        <SessionsModeToggle active="client" />
      </div>

      <div className="flex gap-5">
        {/* Left: Clients list */}
        <div className="w-72 shrink-0 bg-white border border-slate-200 rounded-xl px-4 py-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-bold text-[#08148C] uppercase tracking-widest">Clients</p>
            <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-[#08148C] min-w-[20px] text-center">
              {clients.length}
            </span>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search client..."
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08148C]/20"
            />
          </div>
          <div className="max-h-[65vh] overflow-y-auto space-y-1.5 pr-1">
            {filtered.length === 0 && (
              <p className="text-xs text-slate-400 py-4 text-center">No clients match.</p>
            )}
            {/* Rendered as a real <button>: role="button" on a div satisfies
                S6848 but then trips S6819. Children are phrasing content only,
                so the native tag is valid. Tailwind preflight strips button
                chrome; w-full/text-left restore the block-like layout. */}
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={selectedId === c.id}
                onClick={() => setSelectedId(c.id)}
                className={`w-full text-left cursor-pointer rounded-lg border px-3 py-2.5 transition-colors ${
                  selectedId === c.id
                    ? "bg-blue-50 border-blue-200"
                    : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                }`}
              >
                <span className="flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-800">{c.name || c.id}</span>
                  <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-teal-100 text-[#08148C] min-w-[20px] text-center">
                    {c.logCount}
                  </span>
                </span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  {c.role} · {c.devicesTouched} device{c.devicesTouched === 1 ? "" : "s"}
                  {alarmsSummary(c.alarmsHandled)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Right: Selected client's activity logs */}
        <div className="flex-1 bg-white border border-slate-200 rounded-xl px-6 py-5">
          {!selectedClient ? (
            <p className="text-sm text-slate-400 py-6 text-center">Select a client to view activity logs.</p>
          ) : (
            <>
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{selectedClient.name || selectedClient.id} · Activity Logs</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedClient.logCount} logs · {selectedClient.devicesTouched} device{selectedClient.devicesTouched === 1 ? "" : "s"} touched
                  </p>
                </div>
                <button
                  disabled
                  title="Log bundle download is not yet available for Activity Sessions."
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-200 text-slate-400 rounded-lg px-3 py-1.5 cursor-not-allowed"
                >
                  &#8659; Download bundle
                </button>
              </div>

              <div className="mt-5 space-y-3">
                {logsLoading && <PageLoader />}
                {!logsLoading && logs.length === 0 && (
                  <p className="text-xs text-slate-400 py-6 text-center">No activity logs for this client.</p>
                )}
                {!logsLoading && logs.length > 0 && (
                  logs.map((row) => (
                    <div key={row.sessionId} className="border border-slate-200 rounded-lg p-4 bg-white">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-bold font-mono text-slate-800">{row.sessionId}</span>
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-[#08148C]">
                            {row.channel}
                          </span>
                          <span className="text-xs text-slate-400">{formatLogDate(row.date)}</span>
                        </div>
                        <button
                          onClick={() => navigate(`/sessions/clients/${selectedClient.id}/logs/${row.sessionId}`)}
                          className="text-xs font-semibold text-[#08148C] hover:underline shrink-0"
                        >
                          Open &rarr;
                        </button>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">{row.summary}</p>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
